import { useMemo, useState, type ReactNode } from 'react'
import { Pencil, Plus, Trash2 } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import { Dialog } from '@/components/ui/dialog'
import type { AdminColumn, Fetcher, RowAction } from '@/components/DataTable/types'
import { PERMISSIONS } from '@/lib/permissions'
import { useToast } from '@/providers/UiProviders'
import {
  secCreate,
  secDelete,
  secList,
  secUpdate,
  SEC_STR as S,
  type SecRow,
} from './securityMoreApi'

/**
 * Generic A18 security-registry CRUD table. The DDoS-rules / WAF / vuln-scan /
 * dependency-audit / pentest screens are all "list + add-form + edit + delete"
 * over an admin-curated `sec_*` registry (migration 000248), so one component
 * backs them all. Backend: GET/POST/PUT/DELETE `/admin/security/*`; list is
 * fail-soft empty until the row exists. Placeholder view permission AUDIT_VIEW
 * (mutations are admin-gated server-side by RequireAdmin).
 */
export type SecFieldType = 'text' | 'number' | 'select' | 'checkbox'

export interface SecField {
  /** Yuboriladigan `body` kaliti. */
  name: string
  placeholder: string
  type?: SecFieldType
  mono?: boolean
  /** `select` uchun variantlar. */
  options?: { value: string; label: string }[]
  /** Boshlang'ich qiymat (select/checkbox uchun foydali). */
  defaultValue?: string
}

export interface SecCrudTableProps<T extends SecRow> {
  screenId: string
  title: string
  addLabel: string
  /** Backend yo'li, masalan `/admin/security/waf-rules`. */
  path: string
  /** Ro'yxat javobidagi massiv kaliti, masalan `rules` / `scans` / `findings`. */
  listKey: string
  /** Tez qidiruvda solishtiriladigan maydonlar. */
  searchKeys: string[]
  fields: SecField[]
  columns: AdminColumn<T>[]
  icon: ReactNode
  /** `canAdd` shu maydon to'ldirilganda faollashadi (default: fields[0]). */
  requiredField?: string
  /** Jadval ustida ixtiyoriy panel (DDoS status) — reloadKey o'zgarganda qayta chizadi. */
  topPanel?: (reloadKey: number) => ReactNode
}

/** Bo'sh formani maydon defaultlaridan quradi. */
function blankForm(fields: SecField[]): Record<string, string> {
  const f: Record<string, string> = {}
  for (const fld of fields) {
    if (fld.defaultValue !== undefined) f[fld.name] = fld.defaultValue
    else if (fld.type === 'checkbox') f[fld.name] = 'false'
    else if (fld.type === 'select') f[fld.name] = fld.options?.[0]?.value ?? ''
    else f[fld.name] = ''
  }
  return f
}

/** Formani API `body` ga aylantiradi (tur bo'yicha). */
function toBody(fields: SecField[], form: Record<string, string>): Record<string, unknown> {
  const body: Record<string, unknown> = {}
  for (const f of fields) {
    const raw = form[f.name] ?? ''
    if (f.type === 'number') body[f.name] = Number(raw) || 0
    else if (f.type === 'checkbox') body[f.name] = raw === 'true'
    else body[f.name] = raw.trim()
  }
  return body
}

function FieldInput({
  field,
  value,
  onChange,
}: {
  field: SecField
  value: string
  onChange: (v: string) => void
}) {
  const base =
    'rounded-md border border-border-default bg-surface-1 px-3 py-1.5 text-sm text-primary outline-none focus:border-border-strong'
  if (field.type === 'checkbox') {
    return (
      <label className="inline-flex items-center gap-2 text-sm text-primary">
        <input
          type="checkbox"
          checked={value === 'true'}
          onChange={(e) => onChange(e.target.checked ? 'true' : 'false')}
          className="h-4 w-4 accent-accent"
        />
        {field.placeholder}
      </label>
    )
  }
  if (field.type === 'select') {
    return (
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`${base} px-2`}
        aria-label={field.placeholder}
      >
        {field.options?.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    )
  }
  return (
    <input
      value={value}
      type={field.type === 'number' ? 'number' : 'text'}
      onChange={(e) => onChange(e.target.value)}
      placeholder={field.placeholder}
      spellCheck={false}
      className={`min-w-[150px] flex-1 ${base} ${field.mono ? 'font-mono' : ''}`}
    />
  )
}

export function SecCrudTable<T extends SecRow>({
  screenId,
  title,
  addLabel,
  path,
  listKey,
  searchKeys,
  fields,
  columns,
  icon,
  requiredField,
  topPanel,
}: SecCrudTableProps<T>) {
  const { toast } = useToast()
  const [reloadKey, setReloadKey] = useState(0)
  const [form, setForm] = useState<Record<string, string>>(() => blankForm(fields))
  const [busy, setBusy] = useState(false)
  const [editing, setEditing] = useState<T | null>(null)
  const [editForm, setEditForm] = useState<Record<string, string>>({})

  const reqField = requiredField ?? fields[0]?.name ?? ''
  const canAdd = Boolean(form[reqField]?.trim())
  const reload = () => setReloadKey((k) => k + 1)

  const add = async () => {
    if (!canAdd || busy) return
    setBusy(true)
    try {
      await secCreate(path, toBody(fields, form))
      toast(S.createdGeneric, { tone: 'success' })
      setForm(blankForm(fields))
      reload()
    } catch {
      toast(S.failed, { tone: 'error' })
    } finally {
      setBusy(false)
    }
  }

  const openEdit = (row: T) => {
    const f: Record<string, string> = {}
    for (const fld of fields) {
      const v = row[fld.name]
      f[fld.name] =
        fld.type === 'checkbox' ? (v ? 'true' : 'false') : v === undefined || v === null ? '' : String(v)
    }
    setEditForm(f)
    setEditing(row)
  }

  const saveEdit = async () => {
    if (!editing || busy) return
    if (!editForm[reqField]?.trim()) {
      toast(S.failed, { tone: 'error' })
      return
    }
    setBusy(true)
    try {
      await secUpdate(path, editing.id, toBody(fields, editForm))
      toast(S.savedGeneric, { tone: 'success' })
      setEditing(null)
      reload()
    } catch {
      toast(S.failed, { tone: 'error' })
    } finally {
      setBusy(false)
    }
  }

  const rowActions = useMemo<RowAction<T>[]>(
    () => [
      {
        id: 'edit',
        label: S.edit,
        icon: <Pencil size={14} />,
        onSelect: (r) => openEdit(r),
      },
      {
        id: 'delete',
        label: S.del,
        icon: <Trash2 size={14} />,
        danger: true,
        onSelect: async (r) => {
          try {
            await secDelete(path, r.id)
            toast(S.deletedGeneric, { tone: 'success' })
            reload()
          } catch {
            toast(S.failed, { tone: 'error' })
          }
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [path, toast, fields],
  )

  const searchKeyStr = searchKeys.join('|')
  const fetcher = useMemo<Fetcher<T>>(
    () => secList<T>(path, listKey, searchKeyStr.split('|')),
    [path, listKey, searchKeyStr],
  )

  return (
    <>
      <PageHeader title={title} screenId={screenId} />
      {topPanel?.(reloadKey)}
      <div className="flex flex-wrap items-center gap-2 p-4 pb-0">
        {fields.map((f) => (
          <FieldInput
            key={f.name}
            field={f}
            value={form[f.name] ?? ''}
            onChange={(v) => setForm((s) => ({ ...s, [f.name]: v }))}
          />
        ))}
        <button
          type="button"
          onClick={add}
          disabled={busy || !canAdd}
          className="inline-flex items-center gap-1.5 rounded-md bg-accent px-3 py-1.5 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
        >
          <Plus size={14} />
          {addLabel}
        </button>
      </div>

      <DataTable<T>
        key={reloadKey}
        screenId={screenId}
        permission={PERMISSIONS.AUDIT_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => String(r.id)}
        rowActions={rowActions}
        exportable
        emptyState={{ icon }}
      />

      <Dialog
        open={editing !== null}
        onClose={() => setEditing(null)}
        title={S.editEntry}
        footer={
          <>
            <button
              type="button"
              onClick={saveEdit}
              disabled={busy}
              className="inline-flex items-center gap-1.5 rounded-md bg-accent px-3 py-1.5 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
            >
              {S.save}
            </button>
            <button
              type="button"
              onClick={() => setEditing(null)}
              className="rounded-md border border-border-default px-3 py-1.5 text-sm text-primary hover:bg-surface-1"
            >
              {S.cancel}
            </button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          {fields.map((f) => (
            <div key={f.name} className="flex flex-col gap-1">
              <span className="text-xs text-secondary">{f.placeholder}</span>
              <FieldInput
                field={f}
                value={editForm[f.name] ?? ''}
                onChange={(v) => setEditForm((s) => ({ ...s, [f.name]: v }))}
              />
            </div>
          ))}
        </div>
      </Dialog>
    </>
  )
}
